import { reduce, result, first } from 'underscore';
import React, { useCallback, useContext, useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Input, Label, Button } from '@fluentui/react-components';
import { DsaTarget } from '@bingads-webui/campaign-odata-schemas';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { withError, withValidation } from '@bingads-webui-react/validation';
import { withControlErrorHandling, withUnvalidatedControlErrors } from '@bingads-webui-campaign-react/error-handling';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import {
  DSA_TARGET_OPERAND_TYPES,
  DSA_TARGET_OPERATOR_TYPES,
  generateKeyAndFormateDsaTarget,
} from '@bingads-webui-campaign-react/search-campaign-data';

import { DefaultBidDescription } from './default-bid-description';
import { EditableFluentList } from '../fluent-list-component';
import { getExactUrlStyles } from './styles/exact-url-style';
import { ValidatedWebsite as WebsiteControl } from '../validated-website';
import { useCurrencyCode } from '../utils';

const ValidatedWebsite = withControlErrorHandling(withUnvalidatedControlErrors(WebsiteControl));
const ValidatedTextInput = withControlErrorHandling(withUnvalidatedControlErrors(withError(withValidation(Input))));

const CustomEditRow = ({
  itemKeys, i18n, classes, item, onItemChange, applyFailCount,
}) => {
  const { conditions } = item || {};
  const condition = first(conditions) || {};
  const currency = useCurrencyCode();

  const [isDuplicate, setIsDuplicate] = useState(false);
  const [alwaysShowError, setAlwaysShowError] = useState(false);

  const initialFailCount = useRef(applyFailCount);

  useEffect(() => {
    setIsDuplicate(item.isValid && itemKeys[item.key] > 1);
  }, [item, itemKeys]);

  useEffect(() => {
    if (!item.isValid && applyFailCount > initialFailCount.current) {
      setAlwaysShowError(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyFailCount]);

  const onValidateUrl = useCallback((res) => {
    const { websites } = res.data;
    if (item) {
      const { key, conditions: newConditions } = generateKeyAndFormateDsaTarget([{ ArgumentOperand: first(websites), Operator: DSA_TARGET_OPERATOR_TYPES.Equals, Operand: DSA_TARGET_OPERAND_TYPES.Url }]);
      onItemChange({
        ...item,
        key,
        conditions: newConditions,
        isConditionValid: res.isValid,
        isValid: res.isValid && item.isBidValid,
      });
    }
  }, [item, onItemChange]);

  const onValidateBid = useCallback((res) => {
    if (item) {
      onItemChange({
        ...item,
        Bid: res.data.Bid,
        isBidValid: res.isValid,
        isValid: res.isValid && item.isConditionValid,
      });
    }
  }, [item, onItemChange]);

  return (
    <div className={classnames(classes.exactUrlEditRowContainer, 'exact-url-edit-row-container')}>
      <div className="exact-url-edit-row">
        <div className="website-edit">
          <ValidatedWebsite
            validationProps={{
              data: [condition.ArgumentOperand],
              onValidate: onValidateUrl,
              stopPropagation: true,
              debounce: 700,
              alwaysShowError,
            }}
            validateOnRender
            useFluentV2
          />
        </div>
        <div className="column-bid-edit">
          <ValidatedTextInput
            id="input-bid"
            validationProps={{
              data: item,
              onValidate: onValidateBid,
              field: 'Bid',
              schema: DsaTarget,
            }}
            i18n={i18n}
          />
        </div>
        <Label>{currency}</Label>
      </div>
      {isDuplicate && <div className="duplicate-error">{i18n.getString(_TL_('Duplicate dynamic ad target'))}</div>}
    </div>
  );
};

CustomEditRow.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  classes: PropTypes.shape({
    exactUrlEditRowContainer: PropTypes.string.isRequired,
  }).isRequired,
  item: PropTypes.shape({
    key: PropTypes.string,
    isValid: PropTypes.bool,
    isBidValid: PropTypes.bool,
    isConditionValid: PropTypes.bool,
  }).isRequired,
  onItemChange: PropTypes.func.isRequired,
  applyFailCount: PropTypes.number.isRequired,
};

export const ExactUrlTarget = ({
  data, onDataChange, itemKeys, setItemKeys, applyFailCount,
}) => {
  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getExactUrlStyles);

  useEffect(() => {
    const newKeys = reduce(data.bufferItems, (acc, item) => {
      acc[item.key] = result(acc, item.key, 0) + 1;
      return acc;
    }, {});
    setItemKeys(newKeys);
  }, [data.bufferItems, setItemKeys]);

  const onListItemChange = useCallback(index => (item) => {
    const newBufferItems = data.bufferItems.slice();
    newBufferItems[index] = item;
    onDataChange({ ...data, bufferItems: newBufferItems });
  }, [data, onDataChange]);

  const onListItemRemove = useCallback(index => () => {
    const removedItemKey = data.bufferItems[index].key;
    const newKeys = { ...data.itemKeys };
    delete newKeys[removedItemKey];

    const newBufferItems = data.bufferItems.slice();
    newBufferItems.splice(index, 1);

    onDataChange({ ...data, bufferItems: newBufferItems, itemKeys: newKeys });
  }, [data, onDataChange]);

  const onRemoveAll = useCallback(() => {
    onDataChange({ ...data, bufferItems: [], itemKeys: {} });
  }, [data, onDataChange]);

  const onAddButtonClick = useCallback(() => {
    onDataChange({ ...data, bufferItems: [...data.bufferItems, { isBidValid: true }] });
  }, [data, onDataChange]);

  const onRenderHeader = useCallback(() => (
    <div className={classnames(classes.listHeader, 'list-header')}>
      <Label className="url">{i18n.getString(_TL_('URL equals'))}</Label>
      <Label className="bid">{i18n.getString(_TL_('Bid amount'))}</Label>
      <Button
        style={{
          background: 'transparent',
          border: 'none',
          alignItems: 'start',
          marginLeft: '10px',
        }}
        onClick={onRemoveAll}
      >
        {i18n.getString(_TL_('Remove all'))}
      </Button>
    </div>
  ), [classes.listHeader, i18n, onRemoveAll]);

  const onRenderItem = useCallback(({ item, onItemChange }) => (
    <CustomEditRow
      itemKeys={itemKeys}
      i18n={i18n}
      classes={classes}
      item={item}
      onItemChange={onItemChange}
      applyFailCount={applyFailCount}
    />
  ), [classes, i18n, itemKeys, applyFailCount]);

  const onRenderFooter = useCallback(() => (<DefaultBidDescription />), []);

  return (
    <EditableFluentList
      items={data.bufferItems}
      onItemChange={onListItemChange}
      onItemRemove={onListItemRemove}
      onRenderHeader={onRenderHeader}
      onRenderItem={onRenderItem}
      onRenderFooter={onRenderFooter}
      addButtonText={i18n.getString(_TL_('Add URL'))}
      onAddButtonClick={onAddButtonClick}
    />
  );
};

ExactUrlTarget.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onDataChange: PropTypes.func.isRequired,
  itemKeys: PropTypes.shape({}).isRequired,
  setItemKeys: PropTypes.func.isRequired,
  applyFailCount: PropTypes.number.isRequired,
};
